import React from 'react';
import { Typography } from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { Post } from '@app/types/core';
import { isInstructor } from '@core/selectors/classroom';
import { AulaState } from '@core/types/state';
import formatDate from '@core/utils/formatDate';
import ResponsiveEditorReadOnly from '../Shared/ResponsiveEditorReadOnly';
import PostCard, {
  PostCommentsPlaceholder,
  PostReactionsPlaceholder,
  PostTagsPlaceholder,
} from '../Shared/PostCard';

type PostUser = Post['user'] & {
  avatar?: string;
  firstName?: string;
  lastName?: string;
};

type CommonRoomPostCardProps = {
  post: Post;
};

const parseContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const CommonRoomPostCard = ({ post }: CommonRoomPostCardProps) => {
  const author = post.user as PostUser;
  const isEducator = useSelector((state: AulaState) =>
    isInstructor(state, author.objectId)
  );
  const fullName =
    `${author.firstName || ''} ${author.lastName || ''}`.trim() ||
    author.email ||
    'Unknown user';
  const editorState = parseContent(post.content);

  if (!post.content) return null;

  return (
    <PostCard
      ariaLabel={`Post by ${fullName}`}
      author={{
        name: fullName,
        avatarUrl: author.avatar,
        initials: fullName.slice(0, 2).toUpperCase(),
        role: isEducator ? 'Educator' : undefined,
      }}
      timestamp={formatDate(post.scheduledFor || post.createdAt)}
      edited={Boolean(post.editedAt)}
      content={
        editorState ? (
          <ResponsiveEditorReadOnly
            key={post.editedAt || post.createdAt}
            itemId={post.objectId}
            itemType={post.feedType}
            editorState={editorState}
          />
        ) : (
          <Typography component="p" sx={{ m: 0 }}>
            {post.content}
          </Typography>
        )
      }
      tags={<PostTagsPlaceholder />}
      reactions={<PostReactionsPlaceholder />}
      comments={<PostCommentsPlaceholder />}
    />
  );
};

export default CommonRoomPostCard;
